import {
	identity, isEqual,
	isPlainObject, isUndefined, isString, isFunction,
	get as _get
} from 'lodash';
import {EventEmitter} from 'events';

class Toggle extends EventEmitter {
	constructor(key, on, value) {
		super();
		this._key = key;
		this._on = on;
		this._value = value;
	}

	get key() {
		return this._key;
	}

	get isOn() {
		return this._on;
	}

	set isOn(value) {
		this._on = Boolean(value);
	}

	get isOff() {
		return !this._on;
	}

	get value() {
		return this._value;
	}
}

const _UNSET = Symbol('unset');

class BaseToggles extends EventEmitter {
	constructor(toggles, options) {
		super();
		this._options = Object.assign({hash: identity, default: true}, options);
		this._items = new Map();
		this._hashFunction = this._options.hash || identity;
		if (toggles) {
			this.update(toggles);
		}
	}

	get options() {
		return this._options;
	}

	get items() {
		return this._items;
	}

	get size() {
		return this._items.size;
	}

	hashFunction(value) {
		let hashFunction = this._hashFunction.bind(this);
		return hashFunction(value);
	}

	initToggle(key, value) {
		return new Toggle(key, this.options.default, value);
	}

	add(key, value) {
		let len = arguments.length;
		if (len >= 2) {
			// do nothing
			key = arguments[0];
			value = arguments[1];
		} else if (len === 1) {
			key = _UNSET;
			value = arguments[0];
		} else {
			throw new Error('Arguments missing');
		}

		if (value instanceof Toggle) {
			// Take the key from toggle
			key = value.key;
		} else {
			if (key === _UNSET) {
				key = this.hashFunction(value);
			}
			value = this.initToggle(key, value);
		}
		this.items.set(key, value);
		return this;
	}

	// Keep `on` field for existing toggles.
	update(values) {
		if (!values) {
			return;
		}
		let oldItems = Array.from(this._items.values());
		this._items.clear();

		if (Array.isArray(values)) {
			for (let value of values) {
				this.add(value);
			}
		} else if (isPlainObject(values)) {
			for (let k of Object.keys(values)) {
				this.add(k, values[k]);
			}
		} else if (values instanceof Map) {
			for (let [k, v] of values.entries()) {
				this.add(k, v);
			}
		} else {
			return;
		}
		for (let oldItem of oldItems) {
			let k = oldItem.key;
			if (this._items.has(k)) {
				let newItem = this._items.get(k);
				newItem.isOn = oldItem.isOn;
			}
		}
	}

	getToggle(value) {
		// Maybe value is a toggle
		let items = this.items;
		if (value instanceof Toggle) {
			return items.get(value.key);
		}

		if (items.has(value)) {
			return items.get(value);
		}

		let hash = this.hashFunction(value);
		if (items.has(hash)) {
			return items.get(hash);
		}

		for (let item of items.values()) {
			if (isEqual(item, value)) {
				return item;
			}
		}
	}

	get(value) {
		let toggle = this.getToggle(value);
		if (toggle) {
			return toggle.value;
		} else {
			return undefined;
		}
	}

	toggleOn(value) {
		let t = this.getToggle(value);
		if (t) {
			t.isOn = true;
		}
		return this;
	}

	toggleOff(value) {
		let t = this.getToggle(value);
		if (t) {
			t.isOn = false;
		}
		return this;
	}

	toggle(value) {
		let t = this.getToggle(value);
		if (t.isOn) {
			this.toggleOff(t);
		} else {
			this.toggleOn(t);
		}
		return this;
	}

	isOn(value) {
		let t = this.getToggle(value);
		return t.isOn;
	}

	allOn() {
		for (let item of this.items.values()) {
			item.isOn = true;
		}
		return this;
	}

	allOff() {
		for (let item of this.items.values()) {
			item.isOn = false;
		}
		return this;
	}

	getValue(toggle, getter) {
		if (!toggle) {
			return undefined;
		}
		if (isUndefined(getter)) {
			return toggle.value;
		}
		if (isString(getter)) {
			return _get(toggle, getter);
		}
		if (isFunction(getter)) {
			return getter(toggle);
		}
	}

	* toggledOn(getter) {
		for (let toggle of this.items.values()) {
			if (toggle.isOn) {
				yield this.getValue(toggle, getter);
			}
		}
	}

	* toggledOff(getter) {
		for (let toggle of this.items.values()) {
			if (!toggle.isOn) {
				yield this.getValue(toggle, getter);
			}
		}
	}

	* values(getter) {
		for (let toggle of this.items.values()) {
			yield this.getValue(toggle, getter);
		}
	}

	formatValues(getter) {
		return Array.from(this.toggledOn(getter)).join(', ');
	}

	clone() {
		let C = this.constructor;
		let clone = new C(null, this._options);
		for (let [k, v] of this._items) {
			clone._items.set(k, v);
		}
		return clone;
	}
}

class SimpleToggles extends BaseToggles {
}

class Toggles extends BaseToggles {
	constructor(toggles, options) {
		options = Object.assign({notEmpty: false}, options);
		super(toggles, options);
	}

	toggle(value) {
		let toggle = this.getToggle(value);
		let {notEmpty} = this._options;
		toggle.isOn = !toggle.isOn;
		let empty = 0;
		let total = 0;
		for (let item of this.items.values()) {
			total += 1;
			if (!item.isOn) {
				empty += 1;
			}
		}
		if (empty === total && notEmpty) {
			for (let item of this.items.values()) {
				item.isOn = true;
			}
			return this;
		}
		return this;
	}

	toggleOn(value) {
		let t = this.getToggle(value);
		if (t && !t.isOn) {
			this.toggle(t);
		}
		return this;
	}

	toggleOff(value) {
		let t = this.getToggle(value);
		if (t && t.isOn) {
			this.toggle(t.key);
		}
		return this;
	}
}

class Asterisk extends Toggle {
	constructor() {
		super('*', true);
	}

	get value() {
		return this._key;
	}
}

class AsteriskToggles extends Toggles {
	update(values) {
		if (!values) {
			return;
		}
		this._items.clear();
		this._asterisk = new Asterisk();
		this._items.set('*', this._asterisk);
		if (Array.isArray(values)) {
			for (let v of values) {
				this.add(v);
			}
		} else if (isPlainObject(values)) {
			for (let [k, v] of Object.entries(values)) {
				this.add(k, v);
			}
		} else if (values instanceof Map) {
			for (let [k, v] of values.entries()) {
				this.add(k, v);
			}
		}
		this.asterisk();
	}

	asterisk() {
		for (let t of this.items.values()) {
			t.isOn = (t === this._asterisk);
		}
		return this;
	}

	getValue(toggle, getter) {
		if (toggle === this._asterisk) {
			return toggle.value;
		} else {
			return super.getValue(toggle, getter);
		}
	}

	toggle(value) {
		let toggle = this.getToggle(value);
		let asterisk = this._asterisk;
		if (toggle === asterisk) {
			return this.asterisk();
		}

		let s = toggle.isOn = !toggle.isOn;
		let allSame = true;
		// var allOn = true;
		for (let t of this.items.values()) {
			if (t !== asterisk && t.isOn !== s) {
				allSame = false;
				break;
			}
		}

		if (allSame) {
			return this.asterisk();
		} else {
			asterisk.isOn = false;
		}
		return this;
	}

	clone() {
		let c = super.clone();
		c._asterisk = this._asterisk;
		return c;
	}
}

export {Toggle, BaseToggles, Toggles, SimpleToggles, AsteriskToggles};
